<template>
  <div class="tenants-manager" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Tenants Management</h1>
        <p class="page-subtitle">
          <template v-if="isPlatformAdmin">
            Create, manage, and monitor all tenant organizations
          </template>
          <template v-else>
            View and access your assigned tenant organizations
          </template>
        </p>
      </div>
      <button v-if="isPlatformAdmin" @click="createTenant" class="btn-primary">
        <i class="pi pi-plus"></i>
        Create Tenant
      </button>
    </div>

    <!-- Search & Filters -->
    <div class="filters-bar">
      <div class="search-box">
        <i class="pi pi-search"></i>
        <input v-model="searchQuery" type="text" placeholder="Search tenants by name or slug..."
          @input="debouncedSearch" />
      </div>
      <div class="filter-buttons">
        <button
          v-for="status in statusFilters"
          :key="status.value"
          class="filter-btn"
          :class="{ active: activeFilter === status.value }"
          @click="filterByStatus(status.value)"
        >
          {{ status.label }}
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Loading tenants...</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="visibleTenants.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="pi pi-building"></i>
      </div>
      <h3>No Tenants Found</h3>
      <p>Get started by creating your first tenant organization.</p>
      <button @click="createTenant" class="btn-primary">
        <i class="pi pi-plus"></i>
        Create First Tenant
      </button>
    </div>

    <!-- Tenants Table -->
    <div v-else class="tenants-table-container">
      <table class="tenants-table">
        <thead>
          <tr>
            <th>Tenant</th>
            <th>Slug</th>
            <th>Status</th>
            <th>Plan</th>
            <th>Users</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tenant in visibleTenants"
            :key="tenant._id"
            class="tenant-row"
            @click="viewTenant(tenant)"
          >
            <td>
              <div class="tenant-info">
                <div
                  class="tenant-avatar"
                  :style="{ backgroundColor: getTenantColor(tenant.slug) }"
                >
                  {{ getInitials(tenant.name) }}
                </div>
                <div class="tenant-details">
                  <span class="tenant-name">{{ tenant.name }}</span>
                  <span class="tenant-db">{{ tenant.dbName }}</span>
                </div>
              </div>
            </td>
            <td>
              <code class="slug-badge">{{ tenant.slug }}</code>
            </td>
            <td>
              <span class="status-badge" :class="tenant.status">
                {{ tenant.status }}
              </span>
            </td>
            <td>
              <span class="plan-badge" :class="tenant.plan || 'standard'">
                {{ tenant.plan || 'standard' }}
              </span>
            </td>
            <td>
              <span class="users-count">
                <i class="pi pi-users"></i>
                {{ tenant.userCount || 0 }}
              </span>
            </td>
            <td>
              <span class="date-text">{{ formatDate(tenant.createdAt) }}</span>
            </td>
            <td @click.stop>
              <div class="action-buttons">
                <button @click="viewTenant(tenant)" class="action-btn view" title="View Details">
                  <i class="pi pi-eye"></i>
                </button>
                <button @click="editTenant(tenant)" class="action-btn edit" title="Edit">
                  <i class="pi pi-pencil"></i>
                </button>
                <button
                  class="action-btn access"
                  title="Access Tenant"
                  @click="accessTenant(tenant)"
                >
                  <i class="pi pi-external-link"></i>
                </button>
                <!-- Suspend (Admin only, for active tenants) -->
                <button
                  v-if="isPlatformAdmin && tenant.status === 'active'"
                  class="action-btn suspend"
                  title="Suspend"
                  @click.stop="suspendTenant(tenant)"
                >
                  <i class="pi pi-ban"></i>
                </button>
                <!-- Activate (Admin only, for suspended or provisioning tenants) -->
                <button
                  v-if="isPlatformAdmin &&
                   (tenant.status === 'suspended' || tenant.status === 'provisioning')"
                  class="action-btn activate"
                  title="Activate"
                  @click.stop="activateTenant(tenant)"
                >
                  <i class="pi pi-check-circle"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="visibleTenants.length > 0 && isPlatformAdmin" class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
        <i class="pi pi-chevron-left"></i>
      </button>
      <span class="page-info">
        Page {{ currentPage }} of {{ totalPages }}
      </span>
      <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
        <i class="pi pi-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { platformApi } from '@/services/platformApi';

export default {
  name: 'TenantsManager',
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      activeFilter: 'all',
      tenants: [],
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      statusFilters: [
        { label: 'All', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Provisioning', value: 'provisioning' },
        { label: 'Suspended', value: 'suspended' },
      ],
      debounceTimer: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['getUser']),
    isPlatformAdmin() {
      // Check for platform_admin role (from JWT) or platformRole from store
      const user = this.getUser;
      const platformUser = JSON.parse(localStorage.getItem('platform_user') || '{}');
      return user?.platformRole === 'admin'
        || user?.platformRole === 'platform_admin'
        || platformUser?.role === 'platform_admin';
    },
    assignedTenants() {
      return this.getUser?.assignedTenants || [];
    },
    // Filter tenants for trainers (only show assigned tenants)
    visibleTenants() {
      if (this.isPlatformAdmin) {
        return this.tenants;
      }
      // Trainers can only see their assigned tenants
      return this.tenants.filter((t) => this.assignedTenants.includes(t.slug));
    },
  },
  mounted() {
    this.loadTenants();
  },
  methods: {
    async loadTenants() {
      this.loading = true;
      try {
        // Only send params the backend accepts: status, plan, includeDeleted
        const params = {
          status: this.activeFilter !== 'all' ? this.activeFilter : undefined,
        };

        let tenants = await platformApi.getTenants(params);

        // Filter locally by search query (backend doesn't support it yet)
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          tenants = tenants.filter(
            (t) => t.name?.toLowerCase().includes(query) || t.slug?.toLowerCase().includes(query),
          );
        }

        this.tenants = tenants;
        this.totalPages = 1;
      } catch (error) {
        console.error('Failed to load tenants:', error);
      } finally {
        this.loading = false;
      }
    },
    debouncedSearch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadTenants();
      }, 300);
    },
    filterByStatus(status) {
      this.activeFilter = status;
      this.currentPage = 1;
      this.loadTenants();
    },
    createTenant() {
      this.$router.push('/platform/tenants/new');
    },
    viewTenant(tenant) {
      this.$router.push(`/platform/tenants/${tenant.slug}`);
    },
    editTenant(tenant) {
      this.$router.push(`/platform/tenants/${tenant.slug}/edit`);
    },
    accessTenant(tenant) {
      // Generate tenant URL based on current host
      const { hostname, port, protocol } = window.location;
      // Remove 'admin.' prefix to get base domain
      const baseDomain = hostname.replace(/^admin\./, '');
      // Only include port if non-standard (not 80 for http, not 443 for https)
      const isStandardPort = !port || port === '80' || port === '443';
      const tenantPort = isStandardPort ? '' : `:${port}`;
      const tenantUrl = `${protocol}//${tenant.slug}.${baseDomain}${tenantPort}`;
      window.open(tenantUrl, '_blank');
    },
    async suspendTenant(tenant) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Are you sure you want to suspend "${tenant.name}"?`)) return;

      try {
        await platformApi.suspendTenant(tenant.slug);
        this.loadTenants();
      } catch (error) {
        console.error('Failed to suspend tenant:', error);
      }
    },
    async activateTenant(tenant) {
      try {
        await platformApi.activateTenant(tenant.slug);
        this.loadTenants();
      } catch (error) {
        console.error('Failed to activate tenant:', error);
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
        this.loadTenants();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
        this.loadTenants();
      }
    },
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    getTenantColor(slug) {
      const colors = [
        '#e4821a', '#ec4899', '#3b82f6', '#22c55e',
        '#f59e0b', '#ef4444', '#06b6d4', '#10b981',
      ];
      let hash = 0;
      for (let i = 0; i < slug.length; i += 1) {
        // eslint-disable-next-line no-bitwise
        hash = slug.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
/* Light Theme (Default) */
.tenants-manager {
  --text-primary: #1E2019;
  --text-secondary: #666666;
  --text-muted: #64748b;
  --bg-card: #ffffff;
  --bg-card-alt: #f8f8f8;
  --border-color: #e7e7e7;
  --accent-border: rgba(228, 130, 26, 0.2);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  max-width: 1400px;
  margin: 0 auto;
  transition: all 0.3s ease;
}

/* Dark Theme */
.tenants-manager.dark-theme {
  --text-primary: #f5f5f5;
  --text-secondary: #a8a8a0;
  --text-muted: #777777;
  --bg-card: #2a2a24;
  --bg-card-alt: #353530;
  --border-color: rgba(228, 130, 26, 0.2);
  --accent-border: rgba(228, 130, 26, 0.3);
  --shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--text-secondary);
  margin-top: 0.5rem;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  border: none;
  border-radius: 0.75rem;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(228, 130, 26, 0.4);
}

/* Filters Bar */
.filters-bar {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 280px;
  position: relative;
}

.search-box i {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.search-box input::placeholder {
  color: var(--text-muted);
}

.search-box input:focus {
  outline: none;
  border-color: rgba(228, 130, 26, 0.5);
  background: rgba(228, 130, 26, 0.05);
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
}

.filter-btn {
  padding: 0.75rem 1rem;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: rgba(228, 130, 26, 0.1);
  border-color: rgba(228, 130, 26, 0.3);
}

.filter-btn.active {
  background: linear-gradient(135deg, rgba(228, 130, 26, 0.2), rgba(236, 72, 153, 0.1));
  border-color: rgba(228, 130, 26, 0.5);
  color: var(--text-primary);
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: var(--bg-card);
  border: 1px solid var(--accent-border);
  border-radius: 1rem;
}

.loading-state {
  flex-direction: row;
  gap: 1rem;
  color: #e4821a;
  font-size: 1.125rem;
}

.loading-state i {
  font-size: 1.5rem;
}

.empty-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.1);
  border-radius: 50%;
  margin-bottom: 1rem;
}

.empty-icon i {
  font-size: 2rem;
  color: #e4821a;
}

.empty-state h3 {
  font-size: 1.25rem;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
}

/* Table */
.tenants-table-container {
  background: var(--bg-card);
  border: 1px solid var(--accent-border);
  border-radius: 1rem;
  overflow: hidden;
}

.tenants-table {
  width: 100%;
  border-collapse: collapse;
}

.tenants-table th {
  text-align: left;
  padding: 1rem 1.25rem;
  background: var(--bg-card-alt);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--accent-border);
}

.tenants-table td {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
}

.tenant-row {
  cursor: pointer;
  transition: background 0.2s;
}

.tenant-row:hover {
  background: rgba(228, 130, 26, 0.05);
}

.tenant-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.tenant-avatar {
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
}

.tenant-details {
  display: flex;
  flex-direction: column;
}

.tenant-name {
  font-weight: 600;
  color: var(--text-primary);
}

.tenant-db {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.slug-badge {
  background: rgba(228, 130, 26, 0.1);
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.8rem;
  color: #e4821a;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.active {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.status-badge.provisioning {
  background: rgba(234, 179, 8, 0.2);
  color: #fbbf24;
}

.status-badge.suspended {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.plan-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.plan-badge.free {
  background: rgba(148, 163, 184, 0.2);
  color: #666666;
}

.plan-badge.standard {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.plan-badge.premium {
  background: rgba(228, 130, 26, 0.2);
  color: #f9c277;
}

.plan-badge.enterprise {
  background: rgba(236, 72, 153, 0.2);
  color: #f472b6;
}

.users-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
}

.date-text {
  color: var(--text-muted);
  font-size: 0.875rem;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(228, 130, 26, 0.2);
  border-color: rgba(228, 130, 26, 0.5);
  color: var(--text-primary);
}

.action-btn.view:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.5);
}

.action-btn.access:hover {
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.5);
}

.action-btn.suspend:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.5);
  color: #f87171;
}

.action-btn.activate:hover {
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.5);
  color: #4ade80;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1rem;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: rgba(228, 130, 26, 0.2);
  border-color: rgba(228, 130, 26, 0.5);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  color: var(--text-secondary);
  font-size: 0.875rem;
}
</style>
